import { Pencil } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";

import {
  Admin_GetUserDetail_LIST,
  Admin_UploadUserImage,
  Admin_DeleteUserImage,
  Admin_SetMainProfileImage,
  UserProfile_GetByUserId,
  getAvailabilityTypes,
} from "../../../../constants/adminData";
import { apiVariables } from "../../../../services/api/apiVariables";
import { getToken } from "../../../../utils/tokenUtils";
import CommentsModal from "../../../../components/common/CommentsModal";
import LikesModal from "../../../../components/common/LikesModal";
import { UserProfileData, UserProfile } from "../../../../types/admin";
import Toast from "../../../../components/Toast/Toast";
import ConfirmDialog from "../../../../components/ConfirmDialog/confirmDialog";
import { useAppSelector } from "../../../../store";
import { updatePersonalInfo, updateProfessionalInfo, updateBackgroundInfo, setEmail, setUserId } from "../../../../redux/slices/userProfileSlice";
import authService from '../../../../services/api/authService';
import { PhotoImage } from "../../../../components/common/PhotoImage";
import { getSensitiveData, saveSensitiveData } from '../../../../utils/secureStorage';
import { useGalleryPermission } from '../../../../hooks/useGalleryPermission';
import GalleryPermissionDialog from '../../../../components/common/GalleryPermissionDialog';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Camera as CapacitorCamera } from '@capacitor/camera';
import DashboardLayout from "../../../../components/DashboardLayout";
import { useMenuLabels } from '../../../../hooks/useMenuLabels';
import { themes, useTheme } from "@/context/ThemeContext";
// Native plugin that shows the real Android permission dialog.
// Camera.requestPermissions('photos') in Capacitor 8 is a no-op (always
// returns 'granted' without a dialog because Photo Picker is permissionless).
const MediaPermission = registerPlugin<{ requestMedia(): Promise<void> }>('MediaPermission');

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

interface MyUserProfileProps {
  onOpenMenu?: () => void;
  onBack: () => void;
}

export default function MyUserProfile({ onOpenMenu }: MyUserProfileProps) {
   const { theme } = useTheme();
  const [userData, setUserData] = useState<UserProfileData | null>(null);
  const [userProfileData, setProfileUserData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const profile = useAppSelector((state) => state.userProfile);
  const { permissionState, requestAndProceed, retry, resetPermission } = useGalleryPermission();
  const [likeCount, setLikeCount] = useState<number>(0);
  const [likesOpen, setLikesOpen] = useState(false);
  const galleryEarly = (userData && userData.gallery) ? userData.gallery : [];
  const mainPhotoIdEarly = galleryEarly.find((i: any) => i.isProfile)?.imageId?.toString() || '';

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState<number>(0);
  const [skillsModalOpen, setSkillsModalOpen] = useState(false);
  const [availabilityTypeMaster, setAvailabilityTypeMaster] = useState<
    { availabilityTypeId: number; availabilityTypeName: string }[]
  >([]);
  const labels = useMenuLabels();
  const profileLabel = labels.profile;
//alert(profileLabel);
  // Hardcoded for now (you can later replace with API)

  var remainingSkills = 0;

  const loadCommentCount = async (photoId: string) => {
    if (!photoId) {
      setCommentCount(0);
      return;
    }
    try {
      const url = `${apiVariables.PhotoLike}/${photoId}/comments`;
      const token = authToken || (await getToken());
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}profileLabel` },
      });
      if (!res.ok) {
        setCommentCount(0);
        return;
      }
      const json = await res.json();
      const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      setCommentCount(arr.length);
    } catch (err) {
      console.error('Error loading comment count', err);
      setCommentCount(0);
    }
  };

  useEffect(() => {
    loadCommentCount(mainPhotoIdEarly);
  }, [mainPhotoIdEarly]);

  //  Toast State
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning";
  } | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" | "warning"
  ) => {
    setToast({ message, type });
  };
  const [confirmBox, setConfirmBox] = useState<{
    message: string;
    resolve: (value: boolean) => void;
  } | null>(null);

  const showConfirm = (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmBox({ message, resolve });
    });
  };

  const [localUserId, setLocalUserId] = useState("");
  const [authToken, setAuthToken] = useState("");

  useEffect(() => {
    const loadStoredIds = async () => {
      const uid = profile.userId || (await getSensitiveData("userId")) || "";
      const token = profile.authToken || (await getSensitiveData("authToken")) || "";
      setLocalUserId(uid);
      setAuthToken(token);
    };
    loadStoredIds();
  }, [profile]);

  const loadUserProfile = async () => {
    try {
      const response = await Admin_GetUserDetail_LIST(localUserId, authToken);

      if (response.success) {
        setUserData(response.data);

        const photoId = response.data?.photoId;

        if (photoId !== null && photoId !== undefined && photoId !== 0) {
          const count = await getLikesCountByPhotoId(photoId);
          setLikeCount(count);
        } else {
          setLikeCount(0);
        }

        const responseP = await UserProfile_GetByUserId(localUserId);
        if (responseP.success) {

          remainingSkills = response.data?.skills
            ? response.data.skills.length > 3
              ? response.data.skills.length - 3
              : 0
            : 0;
          setProfileUserData(responseP.data);
        }
      } else {
        showToast("Failed to load user profile.", "error");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      showToast("Error loading profile!", "error");
    } finally {
      setLoading(false);
    }
  };

  const getLikesCountByPhotoId = async (photoId: number) => {
    try {
      const url = `${apiVariables.PhotoLikeCount}/${photoId}/likes/count`;
      const token = await getToken();

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error("❌ Failed to load likes count:", response.status);
        return 0;
      }

      const result = await response.json();

      if (typeof result === "number") return result;

      if (typeof result?.data === "number") return result.data;

      if (typeof result?.result === "number") return result.result;

      return 0;
    } catch (error) {
      console.error("Error getting likes count:", error);
      return 0;
    }
  };

  useEffect(() => {
    if (localUserId && authToken) {
      loadUserProfile();
    }
  }, [localUserId, authToken]);

  useEffect(() => {
    let ignore = false;

    const loadAvailabilityTypes = async () => {
      try {
        const response = await getAvailabilityTypes();
        const data = response?.data ?? response;
        const items = Array.isArray(data) ? data : [];
        if (!ignore) {
          setAvailabilityTypeMaster(
            items
              .map((item: any) => ({
                availabilityTypeId: Number(item.availabilityTypeId ?? item.id),
                availabilityTypeName: item.availabilityTypeName ?? item.name ?? item.label ?? '',
              }))
              .filter((item) => item.availabilityTypeId > 0 && item.availabilityTypeName)
          );
        }
      } catch (error) {
        console.error('Failed to load availability types:', error);
      }
    };

    loadAvailabilityTypes();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (userProfileData) {

      setProfileValuesinRedux();
    }
  }, [userProfileData]);

  const formatDateToMMDDYYYY = (value?: string | null): string => {
    if (!value) return "";

    const datePart = value.split("T")[0];
    const [year, month, day] = datePart.split("-");

    if (!year || !month || !day) return "";

    return `${year}-${month}-${day}`;
  };


  const setProfileValuesinRedux = () => {
    if (!userProfileData) return;

    dispatch(setUserId(userProfileData.userId));
    dispatch(setEmail(userProfileData.username));
    const dob = formatDateToMMDDYYYY(userProfileData.dateOfBirth);

    dispatch(updatePersonalInfo({

      firstName: userProfileData.firstName,
      lastName: userProfileData.lastName,
      dateOfBirth: dob,
      profileImage: userProfileData.profileImageUrl || undefined,
      photoId: userData?.photoId || undefined,
      gender: String(userProfileData.genderId) || undefined,
      heightInches: Number(userProfileData.heightInches),
      heightCentimeters: Number(userProfileData.heightCentimeters),
      heightMeters: Number(userProfileData.heightMeters),
      heightFeet: Number(userProfileData.heightFeet),
      heightUnitSystem: userProfileData.heightUnitSystem,
      weight: Number(userProfileData.weightLbs),


    }));

    dispatch(
      updateProfessionalInfo({
        itSpecialtyId: userProfileData.itspecialtyId,
        currentOranizationName: userProfileData.currentOranizationName,
        workType: userProfileData.workTypeId,
        totalExperience: userProfileData.totalExperienceInYears,
        organizationCount: userProfileData.totalCompaniesHaveBeen,

        location: userProfileData.currentLocation
          ? {
            displayName: userProfileData.currentLocation,
            latitude: 0,
            longitude: 0,
          }
          : undefined,

        npiNumber: userProfileData.npiNumber || undefined,
        workShiftID: Number(userProfileData.workShiftId),
        availabilities:userProfileData.availabilities,
      })
    );
    dispatch(updateBackgroundInfo({
      religionId: Number(userProfileData.religionId),
      ethnicityId: Number(userProfileData.ethnicityId),
      maritalStatusId: Number(userProfileData.maritalStatusId),
      shortBio: userProfileData.shortBio || "",
      skills: userProfileData.skills,
      interests:userProfileData.interests,
    }));

  }

  const handleDeleteAccount = async () => {
    const confirmed = await showConfirm(
      "Your account will be permanently deleted and cannot be recovered."
    );

    if (!confirmed) return;

    try {
      const url = `${apiVariables.DeleteUserAccount}/${localUserId}`;
      const token = await getToken();
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Delete failed");
      }

      await authService.deleteAccount();

      showToast("Your account has been deleted successfully.", "success");

      setTimeout(() => {
        window.location.href = "/";
      }, 2000);

    } catch (error) {
      console.error("Delete account error:", error);
      showToast("Failed to delete account. Please try again.", "error");
    }
  };

  const handleUploadClick = async () => {
    const galleryCount = userData?.gallery?.length ?? 0;
    if (galleryCount >= 5) {
      showToast("You already uploaded 5 images. Delete one to upload more.", "warning");
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      // Web: hidden file input works fine with a direct synchronous click
      fileInputRef.current?.click();
      return;
    }

    // Show system permission dialog via native plugin.
    // If the user denies, requestMedia() rejects — we stop and show a toast.
    try {
      await MediaPermission.requestMedia();
    } catch {
      showToast(
        'Photo access denied. Go to Settings → Apps → ISentryx → Permissions to enable it.',
        'error'
      );
      return;
    }

    const maxAllowed = 5 - galleryCount;
    try {
      const result = await CapacitorCamera.pickImages({
        limit: maxAllowed,
        quality: 90,
      });
      if (!result.photos.length) return;

      if (!localUserId || localUserId.length < 36) {
        showToast("Failed to upload. Invalid UserId.", "error");
        return;
      }

      const formData = new FormData();
      formData.append("UserId", localUserId);
      formData.append("IsProfilePhoto", "false");

      for (const photo of result.photos) {
        const url = photo.webPath ?? photo.path;
        if (!url) continue;
        const res = await fetch(url);
        const blob = await res.blob();
        if (blob.size > 5 * 1024 * 1024) {
          showToast("Each image must be under 5MB", "warning");
          continue;
        }
        formData.append("files", blob, `photo.${photo.format}`);
      }

      const response = await Admin_UploadUserImage(formData, authToken);
      if (response.success) {
        showToast("Image uploaded successfully!", "success");
        loadUserProfile();
      } else {
        showToast("Upload failed!", "error");
      }
    } catch (err: any) {
      const msg = String(err?.message ?? err ?? '').toLowerCase();
      if (!msg.includes('cancel') && !msg.includes('no image') && !msg.includes('user cancelled')) {
        showToast(`Gallery error: ${err?.message ?? 'Unknown error'}`, "error");
      }
    }
  };

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!localUserId || localUserId.length < 36) {
      showToast("Failed to upload. Invalid UserId.", "error");
      return;
    }

    const galleryCount = userData?.gallery?.length ?? 0;
    const maxAllowed = 5 - galleryCount;

    if (galleryCount >= 5) {
      showToast("You already uploaded 5 images. Delete one to upload more.", "warning");
      return;
    }

    if (files.length > maxAllowed) {
      showToast(`You can upload only ${maxAllowed} more image(s).`, "warning");
      return;
    }

    const formData = new FormData();
    formData.append("UserId", localUserId);
    formData.append("IsProfilePhoto", "false");

    Array.from(files).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        showToast("Each image must be under 5MB", "warning");
        return;
      }
      formData.append("files", file);
    });

    try {
      const response = await Admin_UploadUserImage(formData, authToken);

      if (response.success) {
        showToast("Image uploaded successfully!", "success");
        loadUserProfile();
      } else {
        showToast("Upload failed!", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Upload error occurred!", "error");
    }
  };


  const handleDeleteImage = async (imageId: string) => {
    const ok = await showConfirm("Are you sure you want to delete this image?");
    if (!ok) return;
    try {
      const response = await Admin_DeleteUserImage(imageId, authToken);

      if (response.success) {
        showToast("Image deleted!", "success");
        loadUserProfile();
      } else {
        showToast("Delete failed!", "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast("Delete error occurred!", "error");
    }
  };


  const handleSetMainImage = async (imageId: number) => {
    try {
      const response = await Admin_SetMainProfileImage(localUserId, imageId, authToken);

      if (response.success) {
        showToast("Profile photo updated!", "success");
        loadUserProfile();
      } else {
        showToast("Failed to update profile photo.", "error");
      }
    } catch (error) {
      console.error("Set main image error:", error);
      showToast("Error updating profile photo!", "error");
    }
  };

  const handleEditProfile = async () => {
    await saveSensitiveData("fromUserProfile", "true");
    navigate("/personal-details");
  };


  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-xl font-semibold">
        Loading profile...
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="h-screen flex items-center justify-center text-xl text-red-500">
        Failed to load profile
      </div>
    );
  }

  const mainPhotoId = mainPhotoIdEarly;

  const allSkills = userProfileData?.skills ?? userData.skills ?? [];
  const skills = (Array.isArray(allSkills) ? allSkills : []).filter(
    (skill: any) => String(skill?.approvalStatus ?? "").toLowerCase() !== "rejected"
  );
  const technologies = userProfileData?.interests ?? profile.backgroundInfo?.interests ?? [];
  const availabilityRows = userProfileData?.availabilities ?? userData.availabilities ?? profile.professionalInfo?.availabilities ?? [];
  const primaryAvailability = Array.isArray(availabilityRows) ? availabilityRows[0] : availabilityRows;
  const availabilityList = Array.isArray(availabilityRows) ? availabilityRows : primaryAvailability ? [primaryAvailability] : [];
  const now = new Date();
  const todayDatePart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const availabilityExceptions = (primaryAvailability?.exceptions ?? []).filter(
    (exception: any) => !exception?.endDate || String(exception.endDate).split('T')[0] >= todayDatePart
  );
  const isAvailable = Boolean(primaryAvailability?.isAvailable ?? primaryAvailability?.availabilityStatus === 'Available');
  const fullName = userData.name || `${userProfileData?.firstName || ''} ${userProfileData?.lastName || ''}`.trim() || 'Profile';
  const role = userData.itSpecialtyName || userData.specialty || profile.professionalInfo?.itSpecialtyName || 'IT Professional';
  const location = userData.currentLocation || userProfileData?.currentLocation || profile.professionalInfo?.location?.displayName || 'Location not added';
  const organization =
    userProfileData?.currentOranizationName ||
    userProfileData?.currentOrganizationName ||
    profile.professionalInfo?.currentOranizationName ||
    'Organization not added';
  const bio = userData.bio || userProfileData?.shortBio || profile.backgroundInfo?.shortBio || 'No bio added yet.';
  const experience = userProfileData?.totalExperienceInYears ?? profile.professionalInfo?.totalExperience;
  const companyCount = userProfileData?.totalCompaniesHaveBeen ?? profile.professionalInfo?.organizationCount;
  const completionFields = [
    Boolean(userProfileData?.firstName || userData.name),
    Boolean(userProfileData?.lastName || userData.name),
    Boolean(role && role !== 'IT Professional'),
    skills.length > 0,
    Boolean(location && location !== 'Location not added'),
    Boolean(primaryAvailability),
    Boolean(bio && bio !== 'No bio added yet.'),
    Boolean(mainPhotoId || userData.profileImage),
  ];
  const completionPercent = Math.round(
    (completionFields.filter(Boolean).length / completionFields.length) * 100
  );
  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
  const getTechnologyName = (item: any) =>
    item.interestName || item.name || item.technologyName || item.customExploreTechnologies || 'Technology';
  const getAvailabilityName = (item: any) =>
    item.availabilityTypeName || item.name || item.label || 'Available';
  const availabilityTypeById = new Map(
    availabilityTypeMaster.map((type) => [type.availabilityTypeId, type.availabilityTypeName])
  );
  const selectedAvailabilityTypeMap = new Map<number | string, string>();
  const selectedAvailabilityLabelKeys = new Set<string>();

  const addAvailabilityDisplayValue = (id: number | null, name?: string) => {
    const label = (name || (id ? availabilityTypeById.get(id) : '') || '').trim();
    if (!label) return;
    const normalizedLabel = label.toLowerCase().replace(/\s+/g, ' ');
    if (selectedAvailabilityLabelKeys.has(normalizedLabel)) return;
    selectedAvailabilityLabelKeys.add(normalizedLabel);
    selectedAvailabilityTypeMap.set(id || label, label);
  };

  availabilityList.forEach((availability: any) => {
    if (Array.isArray(availability?.availabilityTypes)) {
      availability.availabilityTypes.forEach((type: any) => {
        const id = Number(type.availabilityTypeId ?? type.id);
        addAvailabilityDisplayValue(Number.isFinite(id) && id > 0 ? id : null, getAvailabilityName(type));
      });
    }

    if (Array.isArray(availability?.availabilityTypeIds)) {
      availability.availabilityTypeIds.forEach((rawId: any) => {
        const id = Number(rawId);
        addAvailabilityDisplayValue(Number.isFinite(id) && id > 0 ? id : null);
      });
    }

    if (Array.isArray(availability?.availabilityTypeNames)) {
      availability.availabilityTypeNames.forEach((name: string) => addAvailabilityDisplayValue(null, name));
    }

    const rowTypeId = Number(availability?.availabilityTypeId ?? availability?.id);
    addAvailabilityDisplayValue(
      Number.isFinite(rowTypeId) && rowTypeId > 0 ? rowTypeId : null,
      availability?.availabilityTypeName
    );
  });

  const selectedAvailabilityTypes = Array.from(selectedAvailabilityTypeMap.values());

  return (
    <DashboardLayout title={profileLabel}>
      <div className="space-y-5">

        {(permissionState === 'denied' || permissionState === 'permanently-denied') && (
          <GalleryPermissionDialog
            type={permissionState}
            onRetry={retry}
            onDismiss={resetPermission}
          />
        )}

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base" style={{ color: theme.text }}>Your professional identity, skills, and availability.</p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleEditProfile}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover"
           
           >
              <Pencil className="h-4 w-4" />
              Edit Profile
            </button>
          </div>
        </div>

        {/* ── Identity Hero ── */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-full bg-gradient-to-b from-brand-primary to-brand-gray ring-4 ring-brand-primary/10">
              {mainPhotoId ? (
                <PhotoImage
                  photoId={mainPhotoId}
                  alt={fullName}
                  className="h-full w-full object-cover"
                  fallback={
                    <div  className="flex h-full w-full items-center justify-center text-2xl font-semibold text-white">
                      {getInitials(fullName)}
                    </div>
                  }
                />
              ) : (
                <div  className="flex h-full w-full items-center justify-center text-2xl font-semibold text-white">
                  {getInitials(fullName)}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-xl font-bold text-gray-950" style={{ color: theme.text }}>{fullName}</h2>
              <p className="mt-1 text-sm text-gray-600">
                {role} <span className="text-gray-300">•</span> {location} <span className="text-gray-300">•</span> {organization}
              </p>
              <div className="mt-3 max-w-md">
                <div className="mb-1 flex items-center justify-between text-xs font-semibold text-gray-600">
                  <span >Profile Completion</span>
                  <span>{completionPercent}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-brand-primary"
                    style={{ width: `${completionPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Photo + Professional Information ── */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
          <section className="hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm lg:block">
            <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Profile Photo</h2>
            <div className="mx-auto mt-4 w-full max-w-[240px] lg:max-w-none">
              <div className="aspect-square overflow-hidden rounded-lg bg-gradient-to-b from-brand-primary to-brand-gray">
                {mainPhotoId ? (
                  <PhotoImage
                    photoId={mainPhotoId}
                    alt={fullName}
                    className="h-full w-full object-cover"
                    fallback={
                      <div className="flex h-full w-full items-center justify-center text-4xl font-semibold text-white">
                        {getInitials(fullName)}
                      </div>
                    }
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-white">
                    <span className="text-4xl font-semibold" >{getInitials(fullName)}</span>
                    <span className="text-sm text-white/80" style={{ color: theme.text }}>Profile photo not added</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Professional Information</h2>
            <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ['IT Specialty', role],
                ['Total Experience', experience ? `${experience} years` : '0 years'],
                ['Number of Companies Worked At', companyCount ?? '0'],
                ['Current Organization', organization],
                ['Location', location],
                ['Email', userData.email || profile.email || 'Not added'],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>{label}</p>
                  <p className="mt-1 break-words text-sm font-medium text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* ── Skills & Technologies ── */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Skills</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {skills.length > 0 ? (
                skills.map((skill: any) => (
                  <span
                    key={skill.skillId}
                    className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700"
                  >
                    {skill.skillName}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500" style={{ color: theme.text }}>No skills added yet.</p>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Technologies to Explore</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {technologies.length > 0 ? (
                technologies.map((technology: any, index: number) => (
                  <span
                    key={technology.interestId ?? index}
                    className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700"
                  >
                    {getTechnologyName(technology)}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500" style={{ color: theme.text }}>No technologies added yet.</p>
              )}
            </div>
          </section>
        </div>

        {/* ── About Me ── */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>About Me</h2>
          <p className="mt-3 whitespace-pre-line break-words text-sm leading-6 text-gray-700">{bio}</p>
        </section>

        {/* ── Availability ── */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Availability</h2>
            <button
              type="button"
              onClick={() => navigate('/IT-AvailabilityType')}
              className="inline-flex items-center justify-center rounded-lg border border-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-primary/5"
            >
              Edit Availability
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Status</p>
              <span
                className={`mt-2 inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                  isAvailable ? 'bg-green-50 text-green-700 ring-1 ring-green-100' : 'bg-orange-50 text-orange-700 ring-1 ring-orange-100'
                }`}
              >
                {isAvailable ? 'Available' : 'Not Available'}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Default Hours Per Week</p>
              <p className="mt-2 text-sm font-semibold text-gray-900">
                {primaryAvailability?.defaultAvailableHoursPerWeek ?? primaryAvailability?.availableHoursPerWeek ?? 'Not added'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Available For</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedAvailabilityTypes.length > 0 ? (
                  selectedAvailabilityTypes.map((typeName) => (
                    <span
                      key={typeName}
                      className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"
                    >
                      {typeName}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No availability preferences set</span>
                )}
              </div>
            </div>
          </div>
          {availabilityExceptions.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-600">Exceptions</p>
              <div className="mt-2 space-y-2">
                {availabilityExceptions.map((exception: any, index: number) => (
                  <div
                    key={exception.availabilityExceptionId ?? index}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                  >
                    {formatDate(exception.startDate)} – {formatDate(exception.endDate)} &nbsp;·&nbsp; {exception.availableHoursPerWeek} hrs/week
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <CommentsModal isOpen={commentsOpen} onClose={() => setCommentsOpen(false)} photoId={mainPhotoId} readOnly />
      {skillsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">All Skills</h2>
              <button onClick={() => setSkillsModalOpen(false)}>✕</button>
            </div>
            <div className="flex max-h-[300px] flex-wrap gap-2 overflow-y-auto">
              {skills.map((skill: any) => (
                <span
                  key={skill.skillId}
                  className="rounded-full border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
                >
                  {skill.skillName}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      <LikesModal isOpen={likesOpen} onClose={() => setLikesOpen(false)} photoId={mainPhotoId} />
      {confirmBox && (
        <ConfirmDialog
          message={confirmBox.message}
          onCancel={() => {
            confirmBox.resolve(false);
            setConfirmBox(null);
          }}
          onConfirm={() => {
            confirmBox.resolve(true);
            setConfirmBox(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}
